import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronLeft, ChevronRight, AlertCircle } from 'lucide-react';
import { useJob } from '@/hooks/useJobs';
import { JobDetailContent } from './JobDetailContent';
import { cn } from '@/lib/utils';

interface JobQuickViewSheetProps {
  jobId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPrevious?: () => void;
  onNext?: () => void;
  hasPrevious?: boolean;
  hasNext?: boolean;
}

export function JobQuickViewSheet({
  jobId,
  open,
  onOpenChange,
  onPrevious,
  onNext,
  hasPrevious,
  hasNext,
}: JobQuickViewSheetProps) {
  const navigate = useNavigate();
  const { data: job, isLoading, error } = useJob(jobId);

  const handleClose = () => {
    onOpenChange(false);
  };

  // Render loading skeleton
  if (isLoading) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-[calc(100vw-280px)] flex flex-col p-0">
          <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
            <div className="flex items-center gap-4">
              {(onPrevious || onNext) && (
                <div className="flex items-center gap-1">
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled>
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button variant="ghost" size="icon" className="h-7 w-7" disabled>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              )}
              <Skeleton className="h-6 w-32" />
              <SheetTitle className="sr-only">Loading job</SheetTitle>
              <SheetDescription className="sr-only">Job details are loading.</SheetDescription>
            </div>
          </SheetHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4">
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Render error state
  if (error) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-[calc(100vw-280px)] flex flex-col p-0">
          <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
            <SheetTitle className="text-destructive flex items-center gap-2">
              <AlertCircle className="h-5 w-5" />
              Error Loading Job
            </SheetTitle>
            <SheetDescription className="sr-only">An error occurred while loading job details.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 flex items-center justify-center px-6 py-8">
            <div className="text-center">
              <p className="text-muted-foreground mb-4">{(error as Error).message}</p>
              <Button variant="outline" onClick={() => navigate(`/jobs/${jobId}`)}>
                Open Full Details
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  // Render empty state
  if (!job) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-[calc(100vw-280px)] flex flex-col p-0">
          <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
            <SheetTitle>Not Found</SheetTitle>
            <SheetDescription className="sr-only">The requested job could not be found.</SheetDescription>
          </SheetHeader>
          <div className="flex-1 flex items-center justify-center px-6 py-8">
            <p className="text-muted-foreground">Job not found</p>
          </div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-[calc(100vw-280px)] flex flex-col p-0">
        {/* Header with navigation */}
        <SheetHeader className="px-6 py-4 border-b flex-shrink-0">
          <div className="flex items-center gap-4">
            {(onPrevious || onNext) && (
              <div className="flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={onPrevious}
                  disabled={!hasPrevious}
                  title="Previous (←)"
                >
                  <ChevronLeft className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-7 w-7"
                  onClick={onNext}
                  disabled={!hasNext}
                  title="Next (→)"
                >
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            )}
            <SheetTitle className="text-base font-semibold tabular-nums">
              {job.job_number}
            </SheetTitle>
          </div>
          <SheetDescription className="sr-only">
            Quick view for job {job.job_number}.
          </SheetDescription>
        </SheetHeader>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          <JobDetailContent 
            job={job} 
            isSheet={true} 
            onDeleted={handleClose}
          />
        </div>
      </SheetContent>
    </Sheet>
  );
}
